import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import scrollingIndicator from './scrolling_indicator.png';
import maestroReminderBackground from './maestro_reminder_bg.png';
import {
  CREATE_CUSTOM_POPUP,
  DELETE_CUSTOM_POPUP,
  GET_CUSTOM_POPUP_ASSETS,
  GET_CUSTOM_POPUPS,
  UPDATE_CUSTOM_POPUP,
  UPLOAD_CUSTOM_POPUP_ASSET,
} from './graphql';

type LocaleTextMap = Record<string, string>;
type PopupPlacement = 'MAIN_MENU' | 'SHOP' | 'PROFILE' | 'SETTINGS';
type ButtonAction = 'DISMISS' | 'OPEN_URL' | 'OPEN_SCREEN';
type PopupTemplate = 'DEFAULT' | 'CHAINED_OFFER_REMINDER' | 'MAESTRO_REMINDER';
export type QuizMode =
  | 'TURN'
  | 'LIVE_PLAY'
  | 'MARATHON'
  | 'TOURNAMENT'
  | 'LIVE_EVENT'
  | 'LIVE_SHOW'
  | 'DAILY_BONUS'
  | 'HURDLE';
type PayerStatus = 'ALL' | 'PAYER' | 'NON_PAYER';
type VipStatus = 'ALL' | 'VIP' | 'NON_VIP';
export type DontShowAgainPolicy = 'SESSION' | 'DAY' | 'FOREVER' | 'NONE';
type PopupUiTheme = 'DEFAULT' | 'VINYL';
type PopupPlatform = 'ALL' | 'IOS' | 'ANDROID';
type MatcherConditionOp = '<=' | '>=' | '==' | '!=' | '<' | '>';
type MatcherCondition = { op: MatcherConditionOp; value: string | number };

type CustomPopupAsset = {
  url: string;
  filename: string;
  updatedAt?: string | null;
};

export type CustomPopupConfig = {
  id: string;
  name: string;
  enabled: boolean;
  place: PopupPlacement;
  titleTexts?: LocaleTextMap | null;
  bodyTexts?: LocaleTextMap | null;
  buttonTexts?: LocaleTextMap | null;
  buttonAction: ButtonAction;
  buttonUrl?: string | null;
  isSurveyLink?: boolean | null;
  backgroundImageUrl?: string | null;
  secondaryButtonTexts?: LocaleTextMap | null;
  showSecondaryButton?: boolean | null;
  dontShowAgainTexts?: LocaleTextMap | null;
  dontShowAgainPolicy?: DontShowAgainPolicy | null;
  uiTheme?: PopupUiTheme | null;
  dailyCap: number;
  targetUserIds?: string[] | null;
  platform?: 'IOS' | 'ANDROID' | null;
  minAppVersion?: string | null;
  maxAppVersion?: string | null;
  versionConditions?: MatcherCondition[] | null;
  countries?: string[] | null;
  languages?: string[] | null;
  minLevel?: number | null;
  maxLevel?: number | null;
  levelConditions?: MatcherCondition[] | null;
  payerStatus: PayerStatus;
  vipStatus: VipStatus;
  startDate?: number | null;
  endDate?: number | null;
  template?: PopupTemplate | null;
  reshowAfterQuizzes?: number | null;
  reshowQuizModes?: QuizMode[] | null;
};

const PLACEMENTS: PopupPlacement[] = ['MAIN_MENU', 'SHOP', 'PROFILE', 'SETTINGS'];

// Non-DEFAULT templates render a code-owned layout fed by live game data; the
// config then only drives targeting/scheduling plus text overrides.
const TEMPLATES: { value: PopupTemplate; label: string }[] = [
  { value: 'DEFAULT', label: 'Default (admin-authored content)' },
  { value: 'CHAINED_OFFER_REMINDER', label: 'Chained Offer Reminder' },
  { value: 'MAESTRO_REMINDER', label: 'Songpop Maestro Reminder' },
];

// Quiz modes the game reports (same list as CustomPopupQuizMode on the server). A new mode needs
// a game update first: the game has to report it before picking it here does anything.
export const QUIZ_MODES: { value: QuizMode; label: string }[] = [
  { value: 'TURN', label: 'Turn quiz (incl. special event)' },
  { value: 'LIVE_PLAY', label: 'Live Play' },
  { value: 'MARATHON', label: 'Marathon' },
  { value: 'TOURNAMENT', label: 'Tournament' },
  { value: 'LIVE_EVENT', label: 'Live Event' },
  { value: 'LIVE_SHOW', label: 'Live Show' },
  { value: 'DAILY_BONUS', label: 'Daily Bonus quiz' },
  { value: 'HURDLE', label: 'Hurdle' },
];
// What the server counts when no mode is picked.
export const DEFAULT_QUIZ_MODES: QuizMode[] = ['TURN', 'LIVE_PLAY', 'MARATHON', 'TOURNAMENT', 'LIVE_EVENT', 'LIVE_SHOW'];
// Templates edited from their feature's own screen (scopedTemplate), not from the generic list.
const TEMPLATES_MANAGED_ELSEWHERE: PopupTemplate[] = ['MAESTRO_REMINDER'];

// Starting values when a new popup switches to the Maestro reminder (the reminder rules).
const MAESTRO_REMINDER_DEFAULTS = { dailyCap: '10', reshowAfterQuizzes: '3' };

// Save-time validation failure that also points at the offending form field so the
// editor can highlight it (the page-level Alert is hidden behind the editor modal).
class ValidationError extends Error {
  field?: string;

  constructor(message: string, field?: string) {
    super(message);
    this.field = field;
  }
}

const withField = <T,>(field: string, compute: () => T): T => {
  try {
    return compute();
  } catch (error) {
    throw new ValidationError(error instanceof Error ? error.message : String(error), field);
  }
};

const formatJson = (value: LocaleTextMap | null | undefined) => JSON.stringify(value ?? { en: '' }, null, 2);

const parseLocaleTextMap = (value: string, fieldName: string): LocaleTextMap => {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    throw new Error(`${fieldName} must be valid JSON.`);
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error(`${fieldName} must be a JSON object like {"en":"..."}.`);
  }

  const entries = Object.entries(parsedValue).filter(
    ([locale, text]) => typeof locale === 'string' && typeof text === 'string'
  );

  if (!entries.length) {
    throw new Error(`${fieldName} must contain at least one locale entry.`);
  }

  return Object.fromEntries(entries);
};

const parseOptionalLocaleTextMap = (value: string, fieldName: string): LocaleTextMap | null => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '{}') {
    return null;
  }
  const parsed = parseLocaleTextMap(value, fieldName);
  return Object.values(parsed).some((text) => text.trim()) ? parsed : null;
};

const parseList = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseOptionalInt = (value: string, fieldName: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${fieldName} must be a positive whole number.`);
  }
  return parsed;
};

const CONDITION_OPS: MatcherConditionOp[] = ['<=', '>=', '==', '!=', '<', '>'];

type ConditionRow = { op: MatcherConditionOp; value: string };

const conditionsToRows = (conditions?: MatcherCondition[] | null): ConditionRow[] =>
  (conditions ?? []).map((condition) => ({ op: condition.op, value: String(condition.value) }));

// Old rows only have min/max values - show them as the equivalent conditions.
const legacyRangeToRows = (min?: string | number | null, max?: string | number | null): ConditionRow[] => {
  const rows: ConditionRow[] = [];
  if (min != null && min !== '') {
    rows.push({ op: '>=', value: String(min) });
  }
  if (max != null && max !== '') {
    rows.push({ op: '<=', value: String(max) });
  }
  return rows;
};

// Empty-value rows are ignored; all remaining conditions must match (AND).
const rowsToConditions = (
  rows: ConditionRow[],
  fieldName: string,
  valueKind: 'version' | 'level'
): MatcherCondition[] =>
  rows
    .filter((row) => row.value.trim())
    .map((row) => {
      const raw = row.value.trim();
      if (valueKind === 'level') {
        const parsed = Number(raw);
        if (!Number.isInteger(parsed) || parsed < 0) {
          throw new Error(`${fieldName}: "${raw}" must be a positive whole number.`);
        }
        return { op: row.op, value: parsed };
      }
      if (!/^[0-9]+(\.[0-9]+)*$/.test(raw)) {
        throw new Error(`${fieldName}: "${raw}" must be a version like 003.013.000.`);
      }
      return { op: row.op, value: raw };
    });

type ConditionListEditorProps = {
  label: string;
  rows: ConditionRow[];
  placeholder: string;
  isInvalid?: boolean;
  onChange: (rows: ConditionRow[]) => void;
};

const ConditionListEditor = ({ label, rows, placeholder, isInvalid, onChange }: ConditionListEditorProps) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    {rows.map((row, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
        <Form.Control
          as="select"
          style={{ width: 80, flexShrink: 0 }}
          value={row.op}
          onChange={(event) => {
            const next = [...rows];
            next[index] = { ...next[index], op: event.currentTarget.value as MatcherConditionOp };
            onChange(next);
          }}
        >
          {CONDITION_OPS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </Form.Control>
        <Form.Control
          value={row.value}
          placeholder={placeholder}
          isInvalid={isInvalid}
          onChange={(event) => {
            const next = [...rows];
            next[index] = { ...next[index], value: event.target.value };
            onChange(next);
          }}
        />
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
        >
          ✕
        </Button>
      </div>
    ))}
    <Button size="sm" variant="outline-primary" onClick={() => onChange([...rows, { op: '>=', value: '' }])}>
      + Add condition
    </Button>
    {rows.length > 1 && <div className="text-muted small mt-1">All conditions must match (AND).</div>}
  </Form.Group>
);

const previewText = (json: string, fallback: string): string => {
  try {
    const parsed = JSON.parse(json);
    const value = parsed?.en ?? Object.values(parsed ?? {})[0];
    return typeof value === 'string' && value.trim() ? value : fallback;
  } catch {
    return fallback;
  }
};

// Mirrors the app's <color=#hex>...</color> body highlights in the preview.
const renderColoredPreviewText = (text: string): React.ReactNode[] => {
  const regex = /<color=(#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|[a-zA-Z]+)>([\s\S]*?)<\/color>/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match = regex.exec(text);
  let key = 0;
  while (match) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <span key={key} style={{ color: match[1] }}>
        {match[2]}
      </span>
    );
    key += 1;
    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

type PopupPreviewProps = {
  titleJson: string;
  bodyJson: string;
  buttonJson: string;
  secondaryJson: string;
  showSecondaryButton: boolean;
  dontShowAgainJson: string;
  dontShowAgainPolicy: DontShowAgainPolicy;
  uiTheme: PopupUiTheme;
  backgroundImageUrl: string;
  template: PopupTemplate;
};

// Sample data so admins can see the shape of the code-owned chain-offer layout;
// in the game every value comes from the player's live chained offer.
const CHAIN_PREVIEW_REWARDS = [
  { icon: '🪙', amount: '200', label: 'COINS' },
  { icon: '🎵', amount: 'DANCE!', label: 'PLAYLIST' },
  { icon: '💎', amount: '150', label: 'DIAMONDS' },
  { icon: '💎', amount: '200', label: 'DIAMONDS' },
];

const ChainedOfferPreviewBody = () => (
  <div style={{ marginBottom: 23 }}>
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, marginBottom: 2 }}>MARATHON SALE</div>
    <div style={{ fontSize: 20, fontWeight: 700, lineHeight: '25px', marginBottom: 10 }}>3/5 Bundles Claimed</div>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(255,255,255,0.15)',
        borderRadius: 12,
        padding: '3px 12px',
        fontSize: 11,
        fontWeight: 700,
        marginBottom: 12,
      }}
    >
      <span role="img" aria-label="timer">
        ⏱
      </span>
      23h 30m left
    </div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
        padding: '0 14px',
      }}
    >
      {CHAIN_PREVIEW_REWARDS.map((reward, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} style={{ background: 'white', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ fontSize: 26, padding: '8px 0 2px' }}>{reward.icon}</div>
          <div style={{ color: '#3D2A66', fontSize: 13, fontWeight: 700 }}>{reward.amount}</div>
          <div
            style={{
              background: '#8A5DE3',
              color: 'white',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: '2px 0',
              marginTop: 4,
            }}
          >
            {reward.label}
          </div>
        </div>
      ))}
    </div>
    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
      Sample data — the game shows the player&apos;s real progress, timer and next bundle
    </div>
  </div>
);

// Sample of the code-owned Maestro reminder layout. In the game the timer and the discount come
// from the live Maestro offer; the stage art is always the one bundled in the app (BG_Reminder.png).
export const MaestroReminderPreview = ({
  titleJson,
  buttonJson,
  secondaryJson,
  showSecondaryButton,
  dontShowAgainJson,
  dontShowAgainPolicy,
}: Pick<
  PopupPreviewProps,
  'titleJson' | 'buttonJson' | 'secondaryJson' | 'showSecondaryButton' | 'dontShowAgainJson' | 'dontShowAgainPolicy'
>) => (
  <div
    style={{ background: '#1A1633', borderRadius: 12, padding: '28px 18px', display: 'flex', justifyContent: 'center' }}
  >
    <div
      style={{
        width: 260,
        borderRadius: 14,
        border: '3px solid #5B6CFF',
        overflow: 'hidden',
        background: `center / cover no-repeat url(${maestroReminderBackground}), linear-gradient(180deg, #5A2FB8 0%, #3247D6 55%, #1E2A9E 100%)`,
        // the game keeps the design's 606x1100 card ratio
        aspectRatio: '606 / 1100',
        display: 'flex',
        flexDirection: 'column',
        color: 'white',
        textAlign: 'center',
        padding: '18px 14px 16px',
      }}
    >
      <div
        style={{ fontSize: 11, fontWeight: 700, paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.25)' }}
      >
        {previewText(titleJson, 'Available only for a limited time')}
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, marginTop: 8, letterSpacing: 0.5 }}>
        SONGPOP <span style={{ background: '#2F7BFF', borderRadius: 12, padding: '1px 10px' }}>MAESTRO</span>
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: 12,
          padding: '3px 14px',
          fontSize: 12,
          fontWeight: 700,
          marginTop: 8,
        }}
      >
        <span role="img" aria-label="timer">
          ⏱
        </span>
        23h 30m left
      </div>
      {/* the stage art is the card background (Background Image) */}
      <div style={{ flex: 1 }} />
      <div style={{ position: 'relative', marginTop: 4 }}>
        <div
          style={{
            height: 32,
            borderRadius: 16,
            background: 'linear-gradient(90deg, #2F7BFF 0%, #2FA8FF 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 13,
            paddingLeft: 34,
          }}
        >
          UNLOCK NOW!
        </div>
        <div
          style={{
            position: 'absolute',
            left: 8,
            top: -6,
            background: '#F2453D',
            fontSize: 10,
            fontWeight: 800,
            lineHeight: '11px',
            padding: '4px 5px 6px',
            borderRadius: '3px 3px 0 0',
          }}
        >
          50%
          <br />
          OFF
        </div>
      </div>
      {showSecondaryButton && (
        <div
          style={{
            height: 30,
            borderRadius: 15,
            border: '1.5px solid #5B6CFF',
            color: '#7FA2FF',
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 12,
          }}
        >
          LATER
        </div>
      )}
      {dontShowAgainPolicy !== 'NONE' && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#7FA2FF' }}>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 3,
              background: '#2F7BFF',
              verticalAlign: 'middle',
              marginRight: 6,
            }}
          />
          {previewText(dontShowAgainJson, "Don't show again this session")}
        </div>
      )}
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
        Sample data — the game shows the live offer&apos;s timer and discount (no discount = no ribbon)
      </div>
    </div>
  </div>
);

// Mirrors sp3/src/features/popup/CustomPopupModal.js (at ~0.76 scale of the
// in-game card) so admins can see the in-game result without launching the app.
// Keep the layout/colors/scroll behavior in sync with the app.
const PopupPreview = ({
  titleJson,
  bodyJson,
  buttonJson,
  secondaryJson,
  showSecondaryButton,
  dontShowAgainJson,
  dontShowAgainPolicy,
  uiTheme,
  backgroundImageUrl,
  template,
}: PopupPreviewProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 });

  const bodyText = previewText(bodyJson, 'Body text');

  const measureBody = () => {
    const element = bodyRef.current;
    if (element) {
      setScrollState({
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
      });
    }
  };

  useEffect(measureBody, [bodyText]);

  const { scrollTop, scrollHeight, clientHeight } = scrollState;
  const isScrollable = clientHeight > 0 && scrollHeight > clientHeight + 6;
  const isScrolledToEnd = scrollTop + clientHeight >= scrollHeight - 6;
  const scrollableHeight = Math.max(1, scrollHeight - clientHeight);
  const thumbHeight = Math.min(85, Math.max(36, (clientHeight / Math.max(1, scrollHeight)) * clientHeight * 1.2));
  const thumbTop = Math.max(
    0,
    Math.min((scrollTop / scrollableHeight) * (clientHeight - thumbHeight), clientHeight - thumbHeight)
  );

  if (template === 'MAESTRO_REMINDER') {
    return (
      <MaestroReminderPreview
        titleJson={titleJson}
        buttonJson={buttonJson}
        secondaryJson={secondaryJson}
        showSecondaryButton={showSecondaryButton}
        dontShowAgainJson={dontShowAgainJson}
        dontShowAgainPolicy={dontShowAgainPolicy}
      />
    );
  }

  const buttonBaseStyle: React.CSSProperties = {
    width: '90%',
    height: 29,
    borderRadius: 13,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
  };

  return (
    <div
      style={{
        background: '#1A1633',
        borderRadius: 12,
        padding: '28px 18px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <style>{'.custom-popup-preview-body::-webkit-scrollbar{display:none}'}</style>
      <div
        style={{
          width: 260,
          borderRadius: 14,
          border: '3px solid #8A5DE3',
          overflow: 'hidden',
          position: 'relative',
          background: 'linear-gradient(273deg, #8b30d9 0%, #4a0fa3 100%)',
          fontFamily: 'inherit',
        }}
      >
        {backgroundImageUrl.trim() && (
          <>
            <img
              src={backgroundImageUrl.trim()}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(51,28,76,0.7) 59%, rgba(51,28,76,0.8) 67%, rgba(66,44,111,1) 85%)',
              }}
            />
          </>
        )}
        <div style={{ position: 'relative', padding: '30px 15px 23px', textAlign: 'center', color: 'white' }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              lineHeight: '23px',
              maxWidth: '70%',
              margin: '0 auto 11px',
            }}
          >
            {previewText(titleJson, template === 'CHAINED_OFFER_REMINDER' ? 'Reminder' : 'Title')}
          </div>
          <div style={{ height: 1, width: '60%', margin: '0 auto 11px', background: 'rgba(255,255,255,0.2)' }} />
          {template === 'CHAINED_OFFER_REMINDER' ? (
            <ChainedOfferPreviewBody />
          ) : (
            <div style={{ position: 'relative', minHeight: 175, marginBottom: 23 }}>
              <div
                ref={bodyRef}
                className="custom-popup-preview-body"
                onScroll={measureBody}
                style={{
                  maxHeight: 152,
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: '14px',
                  color: 'white',
                  padding: '0 17px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {renderColoredPreviewText(bodyText)}
              </div>
              {isScrollable && (
                <div
                  style={{
                    position: 'absolute',
                    right: -9,
                    top: thumbTop,
                    width: 3,
                    height: thumbHeight,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.85)',
                  }}
                />
              )}
              {isScrollable && !isScrolledToEnd && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img src={scrollingIndicator} alt="" style={{ width: 15, height: 14, objectFit: 'contain' }} />
                </div>
              )}
            </div>
          )}
          <div
            style={{
              ...buttonBaseStyle,
              background: 'linear-gradient(90deg, #FF563B 0%, #FF680B 100%)',
            }}
          >
            {previewText(buttonJson, 'OK')}
          </div>
          {showSecondaryButton && (
            <div
              style={{
                ...buttonBaseStyle,
                marginTop: 8,
                border: '2.3px solid #D4BEFF',
                color: '#D4BEFF',
              }}
            >
              {previewText(secondaryJson, 'Check later')}
            </div>
          )}
          {dontShowAgainPolicy !== 'NONE' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginTop: 11,
                fontSize: 12,
                fontWeight: 400,
                color: '#E3D4FF',
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: '1.5px solid #331C4C',
                  background: '#E3D4FF',
                  color: '#331C4C',
                  fontSize: 10,
                  fontWeight: 900,
                  lineHeight: '12px',
                  textAlign: 'center',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              >
                {'\u2713'}
              </span>
              {previewText(dontShowAgainJson, "Don't show again")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Zebra striping for the editor sections: alternating tinted bands so each scope
// is visually separated. The translucent gray works in both light and dark mode.
const sectionStyle = (index: number): React.CSSProperties => ({
  padding: '12px 14px 4px',
  borderRadius: 8,
  marginBottom: 12,
  background: index % 2 === 0 ? 'rgba(127, 127, 127, 0.09)' : 'transparent',
});

type CustomPopupsProps = {
  // Only the popups of this template, created with it (e.g. LiveOps → Songpop Maestro → Reminder popup).
  scopedTemplate?: PopupTemplate;
  title?: string;
};

const CustomPopups = ({ scopedTemplate, title = 'Custom Popups' }: CustomPopupsProps) => {
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger' | 'info'>('info');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [place, setPlace] = useState<PopupPlacement>('MAIN_MENU');
  const [titleTextsJson, setTitleTextsJson] = useState(formatJson(null));
  const [bodyTextsJson, setBodyTextsJson] = useState(formatJson(null));
  const [buttonTextsJson, setButtonTextsJson] = useState(formatJson({ en: 'OK' }));
  const [buttonAction, setButtonAction] = useState<ButtonAction>('DISMISS');
  const [buttonUrl, setButtonUrl] = useState('');
  const [isSurveyLink, setIsSurveyLink] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [secondaryButtonTextsJson, setSecondaryButtonTextsJson] = useState(formatJson({ en: 'Check later' }));
  const [showSecondaryButton, setShowSecondaryButton] = useState(true);
  const [dontShowAgainTextsJson, setDontShowAgainTextsJson] = useState(
    formatJson({ en: "Don't show again this session" })
  );
  const [dontShowAgainPolicy, setDontShowAgainPolicy] = useState<DontShowAgainPolicy>('SESSION');
  const [uiTheme, setUiTheme] = useState<PopupUiTheme>('DEFAULT');
  const [dailyCap, setDailyCap] = useState('1');
  const [targetUserIdsText, setTargetUserIdsText] = useState('');
  const [platform, setPlatform] = useState<PopupPlatform>('ALL');
  const [versionConditionRows, setVersionConditionRows] = useState<ConditionRow[]>([]);
  const [countriesText, setCountriesText] = useState('');
  const [languagesText, setLanguagesText] = useState('');
  const [levelConditionRows, setLevelConditionRows] = useState<ConditionRow[]>([]);
  const [payerStatus, setPayerStatus] = useState<PayerStatus>('ALL');
  const [vipStatus, setVipStatus] = useState<VipStatus>('ALL');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [template, setTemplate] = useState<PopupTemplate>('DEFAULT');
  const [reshowAfterQuizzes, setReshowAfterQuizzes] = useState('');
  const [reshowQuizModes, setReshowQuizModes] = useState<QuizMode[]>(DEFAULT_QUIZ_MODES);

  const { data, loading, refetch } = useQuery(GET_CUSTOM_POPUPS, {
    fetchPolicy: 'network-only',
  });
  const { data: assetsData, refetch: refetchAssets } = useQuery(GET_CUSTOM_POPUP_ASSETS, {
    skip: !showEditorModal,
    fetchPolicy: 'cache-and-network',
  });
  const [createPopupConfig, { loading: createLoading }] = useMutation(CREATE_CUSTOM_POPUP);
  const [updatePopupConfig, { loading: updateLoading }] = useMutation(UPDATE_CUSTOM_POPUP);
  const [deletePopupConfig, { loading: deleteLoading }] = useMutation(DELETE_CUSTOM_POPUP);
  const [uploadPopupAsset, { loading: uploadLoading }] = useMutation(UPLOAD_CUSTOM_POPUP_ASSET, {
    onCompleted: (result) => {
      const url = result?.uploadCustomPopupAsset?.url;
      if (url) {
        setBackgroundImageUrl(url);
        setStatusVariant('success');
        setStatusMessage('Background image uploaded.');
        refetchAssets();
      }
    },
    onError: (error) => {
      setStatusVariant('danger');
      setStatusMessage(error.message);
    },
  });

  const uploadedAssets: CustomPopupAsset[] = assetsData?.liveOps?.customPopupAssets ?? [];

  const onBackgroundFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      uploadPopupAsset({ variables: { file } });
    }
    // allow re-selecting the same file
    // eslint-disable-next-line no-param-reassign
    event.currentTarget.value = '';
  };

  const popups: CustomPopupConfig[] = useMemo(() => {
    const configs: CustomPopupConfig[] = data?.liveOps?.customPopupConfigs ?? [];
    return configs
      .filter((config) => {
        const configTemplate = config.template ?? 'DEFAULT';
        return scopedTemplate
          ? configTemplate === scopedTemplate
          : !TEMPLATES_MANAGED_ELSEWHERE.includes(configTemplate);
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [data?.liveOps?.customPopupConfigs, scopedTemplate]);

  useEffect(() => {
    if (!statusMessage) {
      return undefined;
    }
    const timeout = setTimeout(() => setStatusMessage(null), 6000);
    return () => clearTimeout(timeout);
  }, [statusMessage]);

  const resetForm = (popup?: CustomPopupConfig) => {
    setName(popup?.name ?? '');
    setEnabled(popup?.enabled ?? true);
    setPlace(popup?.place ?? 'MAIN_MENU');
    setTitleTextsJson(formatJson(popup?.titleTexts));
    setBodyTextsJson(formatJson(popup?.bodyTexts));
    setButtonTextsJson(formatJson(popup?.buttonTexts ?? { en: 'OK' }));
    setButtonAction(popup?.buttonAction ?? 'DISMISS');
    setButtonUrl(popup?.buttonUrl ?? '');
    setIsSurveyLink(popup?.isSurveyLink ?? false);
    setBackgroundImageUrl(popup?.backgroundImageUrl ?? '');
    setSecondaryButtonTextsJson(formatJson(popup?.secondaryButtonTexts ?? { en: 'Check later' }));
    setShowSecondaryButton(popup?.showSecondaryButton ?? true);
    setDontShowAgainTextsJson(formatJson(popup?.dontShowAgainTexts ?? { en: "Don't show again this session" }));
    setDontShowAgainPolicy(popup?.dontShowAgainPolicy ?? 'SESSION');
    setUiTheme(popup?.uiTheme ?? 'DEFAULT');
    setDailyCap(String(popup?.dailyCap ?? 1));
    setTargetUserIdsText((popup?.targetUserIds ?? []).join('\n'));
    setPlatform(popup?.platform ?? 'ALL');
    const versionRows = conditionsToRows(popup?.versionConditions);
    setVersionConditionRows(
      versionRows.length ? versionRows : legacyRangeToRows(popup?.minAppVersion, popup?.maxAppVersion)
    );
    setCountriesText((popup?.countries ?? []).join(', '));
    setLanguagesText((popup?.languages ?? []).join(', '));
    const levelRows = conditionsToRows(popup?.levelConditions);
    setLevelConditionRows(levelRows.length ? levelRows : legacyRangeToRows(popup?.minLevel, popup?.maxLevel));
    setPayerStatus(popup?.payerStatus ?? 'ALL');
    setVipStatus(popup?.vipStatus ?? 'ALL');
    setStartDate(popup?.startDate ? new Date(popup.startDate) : null);
    setEndDate(popup?.endDate ? new Date(popup.endDate) : null);
    setTemplate(popup?.template ?? 'DEFAULT');
    setReshowAfterQuizzes(popup?.reshowAfterQuizzes != null ? String(popup.reshowAfterQuizzes) : '');
    setReshowQuizModes(popup?.reshowQuizModes?.length ? popup.reshowQuizModes : DEFAULT_QUIZ_MODES);
  };

  // A new Maestro reminder starts from its rules (main menu, 10/day, again after 3 quizzes, checkbox for
  // the session).
  const applyTemplateDefaults = (nextTemplate: PopupTemplate) => {
    if (nextTemplate === 'MAESTRO_REMINDER') {
      setPlace('MAIN_MENU');
      setDailyCap(MAESTRO_REMINDER_DEFAULTS.dailyCap);
      setReshowAfterQuizzes(MAESTRO_REMINDER_DEFAULTS.reshowAfterQuizzes);
      setDontShowAgainPolicy('SESSION');
      setShowSecondaryButton(true);
      // Empty = the game's own translated texts ("UNLOCK NOW!", "LATER", "Don't show again this session").
      setTitleTextsJson(formatJson({ en: '' }));
      setButtonTextsJson(formatJson({ en: '' }));
      setSecondaryButtonTextsJson(formatJson({ en: '' }));
      setDontShowAgainTextsJson(formatJson({ en: '' }));
    }
  };

  const onTemplateChange = (nextTemplate: PopupTemplate) => {
    setTemplate(nextTemplate);
    if (isDraft) {
      applyTemplateDefaults(nextTemplate);
    }
  };

  const toggleQuizMode = (mode: QuizMode, checked: boolean) =>
    setReshowQuizModes((current) =>
      checked
        ? QUIZ_MODES.map((entry) => entry.value).filter((value) => value === mode || current.includes(value))
        : current.filter((value) => value !== mode)
    );

  // react-datetime emits a moment for valid picks and the raw string while typing;
  // an emptied input resets the date to null (= immediately / permanent).
  const onScheduleDateChange = (setter: (value: Date | null) => void) => (value: string | moment.Moment) => {
    if (typeof value === 'string') {
      if (!value.trim()) {
        setter(null);
      }
      return;
    }
    setter(value.toDate());
  };

  const onNewPopup = () => {
    setEditorError(null);
    setInvalidField(null);
    setIsDraft(true);
    setEditingId(null);
    resetForm();
    if (scopedTemplate) {
      setTemplate(scopedTemplate);
      applyTemplateDefaults(scopedTemplate);
    }
    setShowEditorModal(true);
  };

  const onEditPopup = (popup: CustomPopupConfig) => {
    setEditorError(null);
    setInvalidField(null);
    setIsDraft(false);
    setEditingId(popup.id);
    resetForm(popup);
    setShowEditorModal(true);
  };

  const onDeletePopup = async (popup: CustomPopupConfig) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete popup "${popup.name}"?`)) {
      return;
    }
    try {
      await deletePopupConfig({ variables: { id: popup.id } });
      setStatusVariant('success');
      setStatusMessage(`Popup "${popup.name}" deleted.`);
      await refetch();
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to delete popup.');
    }
  };

  const buildInput = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new ValidationError('Name is required.', 'name');
    }

    const parsedDailyCap = parseOptionalInt(dailyCap, 'Daily cap');
    if (parsedDailyCap == null || parsedDailyCap < 1) {
      throw new ValidationError('Daily cap must be at least 1.', 'dailyCap');
    }

    const parsedReshowAfterQuizzes = withField('reshowAfterQuizzes', () =>
      parseOptionalInt(reshowAfterQuizzes, 'Show again after')
    );
    if (parsedReshowAfterQuizzes != null && (parsedReshowAfterQuizzes < 1 || parsedReshowAfterQuizzes > 100)) {
      throw new ValidationError('Show again after must be from 1 to 100 quizzes, or empty.', 'reshowAfterQuizzes');
    }
    if (parsedReshowAfterQuizzes != null && !reshowQuizModes.length) {
      throw new ValidationError('Pick at least one quiz mode to count.', 'reshowQuizModes');
    }

    const versionConditions = withField('versionConditions', () =>
      rowsToConditions(versionConditionRows, 'App version conditions', 'version')
    );
    const levelConditions = withField('levelConditions', () =>
      rowsToConditions(levelConditionRows, 'Player level conditions', 'level')
    );

    // The Maestro reminder button always opens the Maestro screen: no action to store.
    const effectiveButtonAction: ButtonAction = template === 'MAESTRO_REMINDER' ? 'DISMISS' : buttonAction;
    const trimmedButtonUrl = buttonUrl.trim();
    if (effectiveButtonAction === 'OPEN_URL' && !trimmedButtonUrl) {
      throw new ValidationError('Button URL is required when the button action is "Open web page".', 'buttonUrl');
    }
    if (effectiveButtonAction === 'OPEN_SCREEN' && !trimmedButtonUrl) {
      throw new ValidationError('Screen route is required when the button action is "Open game screen".', 'buttonUrl');
    }

    if (startDate && endDate && endDate.getTime() <= startDate.getTime()) {
      throw new ValidationError('Schedule end must be after the start.', 'schedule');
    }

    return {
      name: trimmedName,
      enabled,
      place,
      titleTexts: withField('titleTexts', () => parseLocaleTextMap(titleTextsJson, 'Title texts')),
      bodyTexts: withField('bodyTexts', () => parseLocaleTextMap(bodyTextsJson, 'Content texts')),
      buttonTexts: withField('buttonTexts', () => parseLocaleTextMap(buttonTextsJson, 'Button texts')),
      buttonAction: effectiveButtonAction,
      buttonUrl:
        effectiveButtonAction === 'OPEN_URL' || effectiveButtonAction === 'OPEN_SCREEN' ? trimmedButtonUrl : null,
      isSurveyLink: effectiveButtonAction === 'OPEN_URL' ? isSurveyLink : false,
      // The Maestro reminder always uses the stage art bundled in the game.
      backgroundImageUrl: template === 'MAESTRO_REMINDER' ? null : backgroundImageUrl.trim() || null,
      secondaryButtonTexts: withField('secondaryButtonTexts', () =>
        parseOptionalLocaleTextMap(secondaryButtonTextsJson, 'Secondary button texts')
      ),
      showSecondaryButton,
      dontShowAgainTexts: withField('dontShowAgainTexts', () =>
        parseOptionalLocaleTextMap(dontShowAgainTextsJson, "Don't show again texts")
      ),
      dontShowAgainPolicy,
      uiTheme,
      dailyCap: parsedDailyCap,
      targetUserIds: parseList(targetUserIdsText),
      platform: platform === 'ALL' ? null : platform,
      minAppVersion: null,
      maxAppVersion: null,
      versionConditions: versionConditions.length ? versionConditions : null,
      countries: parseList(countriesText).map((country) => country.toUpperCase()),
      languages: parseList(languagesText).map((language) => language.toLowerCase()),
      minLevel: null,
      maxLevel: null,
      levelConditions: levelConditions.length ? levelConditions : null,
      payerStatus,
      vipStatus,
      startDate: startDate ? startDate.getTime() : null,
      endDate: endDate ? endDate.getTime() : null,
      template: template === 'DEFAULT' ? null : template,
      reshowAfterQuizzes: parsedReshowAfterQuizzes,
      reshowQuizModes: parsedReshowAfterQuizzes != null ? reshowQuizModes : null,
    };
  };

  const onSavePopup = async () => {
    setEditorError(null);
    setInvalidField(null);
    try {
      const input = buildInput();
      if (isDraft) {
        const id = `custom-${slugify(input.name) || 'popup'}-${Date.now()}`;
        await createPopupConfig({ variables: { input: { id, ...input } } });
        setStatusMessage(`Popup "${input.name}" created.`);
      } else {
        if (!editingId) {
          throw new Error('No popup selected.');
        }
        await updatePopupConfig({ variables: { input: { id: editingId, ...input } } });
        setStatusMessage(`Popup "${input.name}" updated.`);
      }
      setStatusVariant('success');
      setShowEditorModal(false);
      await refetch();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : 'Failed to save popup.');
      setInvalidField(error instanceof ValidationError ? error.field ?? null : null);
    }
  };

  const describeMatchers = (popup: CustomPopupConfig) => {
    const matchers: string[] = [];
    if (popup.targetUserIds?.length) {
      matchers.push(`${popup.targetUserIds.length} user(s)`);
    }
    if (popup.platform) {
      matchers.push(popup.platform);
    }
    if (popup.versionConditions?.length) {
      matchers.push(`v ${popup.versionConditions.map((c) => `${c.op}${c.value}`).join(' & ')}`);
    } else if (popup.minAppVersion || popup.maxAppVersion) {
      matchers.push(`version ${popup.minAppVersion ?? '*'} - ${popup.maxAppVersion ?? '*'}`);
    }
    if (popup.countries?.length) {
      matchers.push(popup.countries.join(','));
    }
    if (popup.languages?.length) {
      matchers.push(`lang ${popup.languages.join(',')}`);
    }
    if (popup.levelConditions?.length) {
      matchers.push(`level ${popup.levelConditions.map((c) => `${c.op}${c.value}`).join(' & ')}`);
    } else if (popup.minLevel != null || popup.maxLevel != null) {
      matchers.push(`level ${popup.minLevel ?? '*'} - ${popup.maxLevel ?? '*'}`);
    }
    if (popup.payerStatus !== 'ALL') {
      matchers.push(popup.payerStatus === 'PAYER' ? 'PAU' : 'NPAU');
    }
    if (popup.vipStatus !== 'ALL') {
      matchers.push(popup.vipStatus);
    }
    if (popup.startDate || popup.endDate) {
      const from = popup.startDate ? moment.utc(popup.startDate).format('YYYY-MM-DD HH:mm') : 'now';
      const to = popup.endDate ? moment.utc(popup.endDate).format('YYYY-MM-DD HH:mm') : 'permanent';
      matchers.push(`UTC ${from} → ${to}`);
    }
    return matchers.length ? matchers.join(' · ') : 'Everyone';
  };

  const saving = createLoading || updateLoading;

  return (
    <>
      {statusMessage && (
        <Alert variant={statusVariant} onClose={() => setStatusMessage(null)} dismissible>
          {statusMessage}
        </Alert>
      )}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>{title}</span>
          <Button size="sm" onClick={onNewPopup}>
            New Popup
          </Button>
        </Card.Header>
        <Card.Body>
          {!scopedTemplate && (
            <Alert variant="info">
              The Songpop Maestro reminder popup is edited in LiveOps → Songpop Maestro (Reminder popup, below the
              event).
            </Alert>
          )}
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Daily Cap</th>
                  <th>CTA</th>
                  <th>Audience</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {popups.map((popup) => (
                  <tr key={popup.id}>
                    <td>
                      {popup.name}
                      {popup.template && popup.template !== 'DEFAULT' && (
                        <Badge variant="info" className="ml-2">
                          {TEMPLATES.find((entry) => entry.value === popup.template)?.label ?? popup.template}
                        </Badge>
                      )}
                    </td>
                    <td>{popup.place}</td>
                    <td>
                      <Badge variant={popup.enabled ? 'success' : 'secondary'}>
                        {popup.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>
                      {popup.dailyCap}/day
                      {popup.reshowAfterQuizzes != null && (
                        <div className="text-muted small">again every {popup.reshowAfterQuizzes} quizzes</div>
                      )}
                    </td>
                    <td>
                      {popup.template === 'MAESTRO_REMINDER' && 'Maestro screen'}
                      {popup.template !== 'MAESTRO_REMINDER' && popup.buttonAction === 'OPEN_URL' && popup.buttonUrl}
                      {popup.template !== 'MAESTRO_REMINDER' &&
                        popup.buttonAction === 'OPEN_SCREEN' &&
                        `Screen: ${popup.buttonUrl}`}
                      {popup.template !== 'MAESTRO_REMINDER' && popup.buttonAction === 'DISMISS' && 'Dismiss'}
                    </td>
                    <td>{describeMatchers(popup)}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => onEditPopup(popup)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deleteLoading}
                        onClick={() => onDeletePopup(popup)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {!popups.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      {scopedTemplate ? 'No popup yet.' : 'No custom popups yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showEditorModal} onHide={() => setShowEditorModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{isDraft ? 'New Custom Popup' : `Edit: ${name}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form>
                <div style={sectionStyle(0)}>
                  <h6>Basics</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={name}
                          isInvalid={invalidField === 'name'}
                          onChange={(event) => setName(event.target.value)}
                        />
                        {editingId && (
                          <Form.Text muted>
                            Popup ID (cpid for tracking):{' '}
                            <code
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                              title="Click to copy"
                              onClick={() => navigator.clipboard?.writeText(editingId)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  navigator.clipboard?.writeText(editingId);
                                }
                              }}
                            >
                              {editingId}
                            </code>
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                          as="select"
                          value={place}
                          disabled={template === 'MAESTRO_REMINDER'}
                          onChange={(event) => setPlace(event.currentTarget.value as PopupPlacement)}
                        >
                          {PLACEMENTS.map((placement) => (
                            <option key={placement} value={placement}>
                              {placement}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Enabled</Form.Label>
                        <Form.Check
                          type="switch"
                          id="custom-popup-enabled"
                          checked={enabled}
                          onChange={(event) => setEnabled(event.currentTarget.checked)}
                          label={enabled ? 'On' : 'Off'}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Template</Form.Label>
                        <Form.Control
                          as="select"
                          value={template}
                          disabled={!!scopedTemplate}
                          onChange={(event) => onTemplateChange(event.currentTarget.value as PopupTemplate)}
                        >
                          {TEMPLATES.filter(
                            (entry) =>
                              entry.value === template ||
                              (!scopedTemplate && !TEMPLATES_MANAGED_ELSEWHERE.includes(entry.value))
                          ).map((entry) => (
                            <option key={entry.value} value={entry.value}>
                              {entry.label}
                            </option>
                          ))}
                        </Form.Control>
                        {template === 'CHAINED_OFFER_REMINDER' && (
                          <Form.Text className="text-muted">
                            The game renders its own chain-offer layout (progress, timer, next bundle rewards) from live
                            data. It is only shown to players with an active chained offer that still has unclaimed
                            bundles. Content below only overrides the texts.
                          </Form.Text>
                        )}
                        {template === 'MAESTRO_REMINDER' && (
                          <Form.Text className="text-muted">
                            The game renders the Maestro reminder design (stage art, timer and discount from the live
                            Maestro offer). It is only shown to players who do not own Maestro, while an offer is
                            running for them, and only on game versions that know this design. The buttons are always
                            &quot;UNLOCK NOW!&quot; (opens the Maestro screen) and &quot;LATER&quot;, translated by the
                            game. Leave the title or checkbox text empty to use the game&apos;s translated default.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(1)}>
                  <h6>Content</h6>

                  <Form.Group className="mb-3">
                    <Form.Label>Title (JSON per locale)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={titleTextsJson}
                      isInvalid={invalidField === 'titleTexts'}
                      onChange={(event) => setTitleTextsJson(event.target.value)}
                    />
                  </Form.Group>
                  {template === 'DEFAULT' ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Content (JSON per locale)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={bodyTextsJson}
                        isInvalid={invalidField === 'bodyTexts'}
                        onChange={(event) => setBodyTextsJson(event.target.value)}
                      />
                    </Form.Group>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      {template === 'MAESTRO_REMINDER'
                        ? 'The title is the line at the top ("Available only for a limited time"). The rest of the body (Songpop Maestro logo, timer, art, discount) is drawn by the game — there is nothing to author here.'
                        : 'Body content is rendered by the game from live chain-offer data (bundles claimed, time left, next rewards) — there is nothing to author here.'}
                    </Alert>
                  )}
                </div>

                <div style={sectionStyle(2)}>
                  <h6>Buttons</h6>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Button Text (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={buttonTextsJson}
                          disabled={template === 'MAESTRO_REMINDER'}
                          isInvalid={invalidField === 'buttonTexts'}
                          onChange={(event) => setButtonTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Button Action</Form.Label>
                        <Form.Control
                          as="select"
                          value={buttonAction}
                          disabled={template === 'MAESTRO_REMINDER'}
                          onChange={(event) => setButtonAction(event.currentTarget.value as ButtonAction)}
                        >
                          <option value="DISMISS">Dismiss popup</option>
                          <option value="OPEN_URL">Open web page</option>
                          <option value="OPEN_SCREEN">Open game screen</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>{buttonAction === 'OPEN_SCREEN' ? 'Screen route' : 'Button URL'}</Form.Label>
                        <Form.Control
                          value={buttonUrl}
                          disabled={
                            template === 'MAESTRO_REMINDER' ||
                            (buttonAction !== 'OPEN_URL' && buttonAction !== 'OPEN_SCREEN')
                          }
                          isInvalid={invalidField === 'buttonUrl'}
                          placeholder={buttonAction === 'OPEN_SCREEN' ? 'e.g. ChainedOffer' : 'https://...'}
                          onChange={(event) => setButtonUrl(event.target.value)}
                        />
                        <Form.Check
                          type="checkbox"
                          id="custom-popup-survey-link"
                          className="mt-1"
                          checked={isSurveyLink}
                          disabled={buttonAction !== 'OPEN_URL'}
                          onChange={(event) => setIsSurveyLink(event.currentTarget.checked)}
                          label="Survey link (game appends userID & country params)"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Secondary Button</Form.Label>
                        <Form.Check
                          type="switch"
                          id="custom-popup-show-secondary"
                          checked={showSecondaryButton}
                          onChange={(event) => setShowSecondaryButton(event.currentTarget.checked)}
                          label={showSecondaryButton ? 'Shown' : 'Hidden'}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Secondary Button Text (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={secondaryButtonTextsJson}
                          disabled={!showSecondaryButton || template === 'MAESTRO_REMINDER'}
                          isInvalid={invalidField === 'secondaryButtonTexts'}
                          onChange={(event) => setSecondaryButtonTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* The Maestro reminder draws its own design over the stage art bundled in the game. */}
                {template !== 'MAESTRO_REMINDER' && (
                  <div style={sectionStyle(3)}>
                    <h6>Appearance</h6>

                    <Row>
                      <Col md={7}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Background Image (optional, shown behind the text with a dark overlay)
                          </Form.Label>
                          <div className="d-flex align-items-center mb-1">
                            <label
                              htmlFor="custom-popup-bg-upload"
                              className="btn btn-sm btn-outline-primary mb-0 mr-2"
                            >
                              {uploadLoading ? 'Uploading…' : 'Upload from PC'}
                            </label>
                            <input
                              id="custom-popup-bg-upload"
                              type="file"
                              accept="image/*"
                              hidden
                              disabled={uploadLoading}
                              onChange={onBackgroundFileSelected}
                            />
                            {backgroundImageUrl.trim() && (
                              <Button size="sm" variant="outline-danger" onClick={() => setBackgroundImageUrl('')}>
                                Remove
                              </Button>
                            )}
                          </div>
                          <Form.Control
                            value={backgroundImageUrl}
                            placeholder="https://... (or use Upload from PC)"
                            onChange={(event) => setBackgroundImageUrl(event.target.value)}
                          />
                          {uploadedAssets.length > 0 && (
                            <>
                              <div className="text-muted small mt-2 mb-1">Or pick a previously uploaded image:</div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 8,
                                  overflowX: 'auto',
                                  paddingBottom: 4,
                                }}
                              >
                                {uploadedAssets.map((asset) => {
                                  const isSelected = backgroundImageUrl.trim() === asset.url;
                                  return (
                                    <button
                                      key={asset.url}
                                      type="button"
                                      title={asset.filename}
                                      onClick={() => setBackgroundImageUrl(isSelected ? '' : asset.url)}
                                      style={{
                                        width: 54,
                                        height: 88,
                                        flexShrink: 0,
                                        padding: 0,
                                        borderRadius: 6,
                                        border: isSelected ? '3px solid #7a4fd0' : '1px solid #ced4da',
                                        backgroundImage: `url(${asset.url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        cursor: 'pointer',
                                        outline: 'none',
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>UI Theme (colors)</Form.Label>
                          <Form.Control
                            as="select"
                            value={uiTheme}
                            onChange={(event) => setUiTheme(event.currentTarget.value as PopupUiTheme)}
                          >
                            <option value="DEFAULT">Default (standard colors)</option>
                            <option value="VINYL">Vinyl (follows the player&apos;s active vinyl)</option>
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}

                <div style={sectionStyle(4)}>
                  <h6>Don&apos;t Show Again</h6>

                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Suppresses For</Form.Label>
                        <Form.Control
                          as="select"
                          value={dontShowAgainPolicy}
                          onChange={(event) => setDontShowAgainPolicy(event.currentTarget.value as DontShowAgainPolicy)}
                        >
                          <option value="SESSION">Rest of the session</option>
                          <option value="DAY">Rest of the day</option>
                          <option value="FOREVER">Forever</option>
                          <option value="NONE">None (hide the checkbox)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>Checkbox Label (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={dontShowAgainTextsJson}
                          disabled={dontShowAgainPolicy === 'NONE'}
                          isInvalid={invalidField === 'dontShowAgainTexts'}
                          onChange={(event) => setDontShowAgainTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Show again after (quizzes)</Form.Label>
                        <Form.Control
                          value={reshowAfterQuizzes}
                          placeholder="Empty = once per session"
                          isInvalid={invalidField === 'reshowAfterQuizzes'}
                          onChange={(event) => setReshowAfterQuizzes(event.target.value)}
                        />
                        <Form.Text muted>
                          When the player closes the popup without ticking the box, it comes back in the same session
                          after this many quizzes. Daily cap still applies. Needs a game version that supports it; older
                          versions show it once per session.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label className={invalidField === 'reshowQuizModes' ? 'text-danger' : undefined}>
                          Quizzes that count
                        </Form.Label>
                        <div className="d-flex flex-wrap">
                          {QUIZ_MODES.map((mode) => (
                            <Form.Check
                              key={mode.value}
                              type="checkbox"
                              id={`custom-popup-quiz-mode-${mode.value}`}
                              className="mr-3"
                              style={{ minWidth: '45%' }}
                              disabled={!reshowAfterQuizzes.trim()}
                              checked={reshowQuizModes.includes(mode.value)}
                              onChange={(event) => toggleQuizMode(mode.value, event.currentTarget.checked)}
                              label={mode.label}
                            />
                          ))}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(5)}>
                  <h6>Audience matchers (leave empty to match everyone)</h6>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Schedule start (UTC)</Form.Label>
                        <DateTimePicker
                          utc
                          dateFormat="YYYY-MM-DD"
                          timeFormat="HH:mm"
                          inputProps={{
                            placeholder: 'Immediately',
                            className: `form-control${invalidField === 'schedule' ? ' is-invalid' : ''}`,
                          }}
                          onChange={onScheduleDateChange(setStartDate)}
                          value={startDate ?? undefined}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Schedule end (UTC)</Form.Label>
                        <DateTimePicker
                          utc
                          dateFormat="YYYY-MM-DD"
                          timeFormat="HH:mm"
                          inputProps={{
                            placeholder: 'Permanent',
                            disabled: !endDate,
                            className: `form-control${invalidField === 'schedule' ? ' is-invalid' : ''}`,
                          }}
                          onChange={onScheduleDateChange(setEndDate)}
                          value={endDate ?? undefined}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Check
                          type="switch"
                          id="popup-schedule-permanent"
                          label="Permanent (no end)"
                          checked={!endDate}
                          onChange={() =>
                            setEndDate(
                              endDate
                                ? null
                                : moment
                                    .utc(startDate ?? undefined)
                                    .add(7, 'days')
                                    .toDate()
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Daily Cap (shows/day)</Form.Label>
                        <Form.Control
                          value={dailyCap}
                          isInvalid={invalidField === 'dailyCap'}
                          onChange={(event) => setDailyCap(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Platform</Form.Label>
                        <Form.Control
                          as="select"
                          value={platform}
                          onChange={(event) => setPlatform(event.currentTarget.value as PopupPlatform)}
                        >
                          <option value="ALL">Both (iOS &amp; Android)</option>
                          <option value="IOS">iOS only</option>
                          <option value="ANDROID">Android only</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Countries (ISO, comma)</Form.Label>
                        <Form.Control
                          value={countriesText}
                          placeholder="e.g. US, FR (leave empty for all)"
                          onChange={(event) => setCountriesText(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Game languages (ISO, comma)</Form.Label>
                        <Form.Control
                          value={languagesText}
                          placeholder="e.g. en, fr (leave empty for all)"
                          onChange={(event) => setLanguagesText(event.target.value)}
                        />
                        <Form.Text className="text-muted">
                          Language the game is displayed in. Unsupported device languages show English, so
                          &quot;en&quot; also reaches those players. Empty = all languages.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <ConditionListEditor
                        label="App Version Conditions"
                        rows={versionConditionRows}
                        placeholder="003.013.000"
                        isInvalid={invalidField === 'versionConditions'}
                        onChange={setVersionConditionRows}
                      />
                    </Col>
                    <Col md={6}>
                      <ConditionListEditor
                        label="Player Level Conditions"
                        rows={levelConditionRows}
                        placeholder="10"
                        isInvalid={invalidField === 'levelConditions'}
                        onChange={setLevelConditionRows}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>PAU / NPAU</Form.Label>
                        <Form.Control
                          as="select"
                          value={payerStatus}
                          onChange={(event) => setPayerStatus(event.currentTarget.value as PayerStatus)}
                        >
                          <option value="ALL">Everyone</option>
                          <option value="PAYER">PAU (has paid)</option>
                          <option value="NON_PAYER">NPAU (never paid)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>VIP Status</Form.Label>
                        <Form.Control
                          as="select"
                          value={vipStatus}
                          onChange={(event) => setVipStatus(event.currentTarget.value as VipStatus)}
                        >
                          <option value="ALL">Everyone</option>
                          <option value="VIP">VIP (active subscriber)</option>
                          <option value="NON_VIP">Non-VIP</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Target User IDs (one per line, empty = all users)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={targetUserIdsText}
                      onChange={(event) => setTargetUserIdsText(event.target.value)}
                    />
                  </Form.Group>
                </div>
              </Form>
            </Col>
            <Col md={4}>
              <div style={{ position: 'sticky', top: 0 }}>
                <h6 className="text-center mb-2">In-game preview</h6>
                <PopupPreview
                  titleJson={titleTextsJson}
                  bodyJson={bodyTextsJson}
                  buttonJson={buttonTextsJson}
                  secondaryJson={secondaryButtonTextsJson}
                  showSecondaryButton={showSecondaryButton}
                  dontShowAgainJson={dontShowAgainTextsJson}
                  dontShowAgainPolicy={dontShowAgainPolicy}
                  uiTheme={uiTheme}
                  backgroundImageUrl={backgroundImageUrl}
                  template={template}
                />
                {uiTheme === 'VINYL' && (
                  <div className="text-muted small text-center mt-1">
                    Vinyl theme follows the player&apos;s active vinyl in-game; the preview shows the standard vinyl
                    colors.
                  </div>
                )}
                <div className="text-muted small text-center mt-2">
                  {template === 'MAESTRO_REMINDER'
                    ? 'Approximation of the Maestro reminder (en texts). Upload the full stage art (606x1100 ratio, e.g. 1212x2200) as the Background Image: it fills the whole card.'
                    : template === 'CHAINED_OFFER_REMINDER'
                    ? 'Preview shows the generic layout only — in the game this popup uses the chain-offer reminder design (progress, timer, rewards).'
                    : 'Approximation of the in-game popup (en texts). Long content scrolls like in the game.'}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {editorError && (
            <div className="text-danger mr-auto" style={{ maxWidth: '65%' }}>
              {editorError}
            </div>
          )}
          <Button variant="secondary" onClick={() => setShowEditorModal(false)}>
            Cancel
          </Button>
          <Button onClick={onSavePopup} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomPopups;
